import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  HStack,
  VStack,
  Text,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Card,
  CardBody,
  Input,
  Select,
} from "@chakra-ui/react";
import { FiClock, FiCheckCircle, FiXCircle, FiFilter } from "react-icons/fi";
import axios from "axios";
import { BASE_URL } from "../services/api";
import OfferApprovalModal from "../components/OfferApprovalModal";

const OfferApprovalsPage = () => {
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [allApprovals, setAllApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPendingApprovals(),
        fetchApprovalStats(),
        fetchAllApprovals(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      console.log(
        "Fetching pending approvals from:",
        `${BASE_URL}/api/v1/offers/approvals/pending`
      );
      const response = await axios.get(
        `${BASE_URL}/api/v1/offers/approvals/pending`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Pending approvals response:", response.data);
      setPendingApprovals(response.data.data || []);
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
      console.error("Error details:", error.response?.data || error.message);
    }
  };

  const fetchApprovalStats = async () => {
    try {
      console.log(
        "Fetching approval stats from:",
        `${BASE_URL}/api/v1/offers/approvals/stats`
      );
      const response = await axios.get(
        `${BASE_URL}/api/v1/offers/approvals/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Approval stats response:", response.data);
      setStats(response.data.data || null);
    } catch (error) {
      console.error("Error fetching approval stats:", error);
      console.error("Error details:", error.response?.data || error.message);
    }
  };

  const fetchAllApprovals = async () => {
    try {
      console.log(
        "Fetching all approvals from:",
        `${BASE_URL}/api/v1/offers/approvals/all`
      );
      const response = await axios.get(
        `${BASE_URL}/api/v1/offers/approvals/all`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            limit: 100,
          },
        }
      );
      console.log("All approvals response:", response.data);
      console.log(
        "First approval structure:",
        JSON.stringify(response.data.data?.[0], null, 2)
      );
      setAllApprovals(response.data.data || []);
    } catch (error) {
      console.error("Error fetching all approvals:", error);
      console.error("Error details:", error.response?.data || error.message);
    }
  };

  const handleApprovalClick = (offer) => {
    setSelectedOffer(offer);
    onOpen();
  };

  const handleApprovalComplete = () => {
    fetchData();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "yellow",
      approved: "green",
      rejected: "red",
      completed: "blue",
      cancelled: "gray",
      expired: "orange",
    };
    return colors[status] || "gray";
  };

  // Helper function to extract offer data from nested structure
  const extractOfferData = (approval) => {
    // Log the structure to debug
    if (allApprovals.length > 0 && approval === allApprovals[0]) {
      console.log("Extracting data from approval:", approval);
      console.log("approval.offerId:", approval.offerId);
      console.log("approval.offerSnapshot:", approval.offerSnapshot);
    }

    // Check if we have offerSnapshot data (newer approval format)
    if (approval.offerSnapshot) {
      const snapshot = approval.offerSnapshot;
      // Use approval._id as fallback offer ID
      const displayOfferId =
        snapshot.offerId ||
        approval._id?.toString().slice(-8).toUpperCase() ||
        "N/A";

      return {
        offerId: displayOfferId,
        candidateName: snapshot.candidateName || "N/A",
        designation: snapshot.position || "N/A",
        ctc: snapshot.ctc || snapshot.compensation || null,
        totalStages: approval.approvalStages?.length || 0,
      };
    }

    // Check if offerId is null or a string (MongoDB ObjectId that wasn't populated)
    if (!approval.offerId || typeof approval.offerId === "string") {
      console.warn("offerId is not populated:", approval.offerId);
      return {
        offerId: "N/A",
        candidateName: "N/A",
        designation: "N/A",
        ctc: null,
        totalStages: approval.approvalStages?.length || 0,
      };
    }

    // Original nested structure (OfferDetails -> OfferHeader -> Candidate)
    const offerDetails = approval.offerId; // This should be OfferDetails
    const offerHeader = offerDetails?.offerId; // This should be OfferHeader
    const candidate = offerHeader?.candidateId;

    const candidateName =
      candidate?.fullName ||
      `${candidate?.firstName || ""} ${candidate?.lastName || ""}`.trim() ||
      "N/A";

    return {
      offerId: offerHeader?.offerId || "N/A",
      candidateName,
      designation: offerDetails?.designation || "N/A",
      ctc: offerDetails?.ctc,
      totalStages: approval.approvalStages?.length || 0,
    };
  };

  const filteredApprovals = allApprovals.filter((approval) => {
    const { offerId, candidateName } = extractOfferData(approval);

    // Also check in offerSnapshot for more search options
    const snapshotEmail = approval.offerSnapshot?.candidateEmail || "";

    const matchesSearch =
      offerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      snapshotEmail.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" || approval.approvalStatus === filterStatus;

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box textAlign="center" py={10}>
          <Spinner size="xl" />
          <Text mt={4} color="gray.600">
            Loading approvals...
          </Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxW="100%"
      px={{ base: 4, md: 6, lg: 8 }}
      py={{ base: 4, md: 8 }}
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        {/* Header */}
        <Box>
          <Heading size={{ base: "md", md: "lg" }} mb={2}>
            Offer Approvals
          </Heading>
          <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
            Manage and track offer approval workflows
          </Text>
        </Box>

        {/* Stats */}
        {stats && (
          <Card>
            <CardBody p={{ base: 4, md: 6 }}>
              <StatGroup
                flexDirection={{ base: "column", md: "row" }}
                gap={{ base: 4, md: 0 }}
              >
                <Stat>
                  <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                    Pending Approvals
                  </StatLabel>
                  <StatNumber
                    color="orange.500"
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    {stats.pending || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                    Approved This Month
                  </StatLabel>
                  <StatNumber
                    color="green.500"
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    {stats.approvedThisMonth || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                    Rejected This Month
                  </StatLabel>
                  <StatNumber
                    color="red.500"
                    fontSize={{ base: "2xl", md: "3xl" }}
                  >
                    {stats.rejectedThisMonth || 0}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel fontSize={{ base: "xs", md: "sm" }}>
                    Avg. Approval Time
                  </StatLabel>
                  <StatNumber fontSize={{ base: "2xl", md: "3xl" }}>
                    {stats.avgApprovalTime || "N/A"}
                  </StatNumber>
                </Stat>
              </StatGroup>
            </CardBody>
          </Card>
        )}

        {/* Tabs */}
        <Tabs colorScheme="blue" variant={{ base: "enclosed", md: "line" }}>
          <TabList
            overflowX="auto"
            overflowY="hidden"
            css={{
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            <Tab fontSize={{ base: "sm", md: "md" }} whiteSpace="nowrap">
              <HStack spacing={{ base: 1, md: 2 }}>
                <Icon as={FiClock} />
                <Text>Pending ({pendingApprovals.length})</Text>
              </HStack>
            </Tab>
            <Tab fontSize={{ base: "sm", md: "md" }} whiteSpace="nowrap">
              <HStack spacing={{ base: 1, md: 2 }}>
                <Icon as={FiCheckCircle} />
                <Text>All Approvals ({allApprovals.length})</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Pending Approvals Tab */}
            <TabPanel px={{ base: 0, md: 4 }}>
              {pendingApprovals.length === 0 ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    No pending approvals at this time
                  </Text>
                </Alert>
              ) : (
                <Box overflowX="auto" pb={4}>
                  <Table variant="simple" size={{ base: "sm", md: "md" }}>
                    <Thead>
                      <Tr>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Offer ID
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Candidate
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", md: "table-cell" }}
                        >
                          Position
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", lg: "table-cell" }}
                        >
                          CTC
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", lg: "table-cell" }}
                        >
                          Stage
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", xl: "table-cell" }}
                        >
                          Workflow
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", xl: "table-cell" }}
                        >
                          Submitted
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pendingApprovals.map((approval) => {
                        const {
                          offerId,
                          candidateName,
                          designation,
                          ctc,
                          totalStages,
                        } = extractOfferData(approval);

                        return (
                          <Tr key={approval._id}>
                            <Td
                              fontWeight="semibold"
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                            >
                              {offerId}
                            </Td>
                            <Td fontSize={{ base: "xs", md: "sm" }}>
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">{candidateName}</Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  display={{ base: "block", md: "none" }}
                                >
                                  {designation}
                                </Text>
                              </VStack>
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              display={{ base: "none", md: "table-cell" }}
                            >
                              {designation}
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                              display={{ base: "none", lg: "table-cell" }}
                            >
                              {ctc ? `₹${ctc.toLocaleString()}` : "N/A"}
                            </Td>
                            <Td display={{ base: "none", lg: "table-cell" }}>
                              <Text fontSize={{ base: "xs", md: "sm" }}>
                                {approval.currentStage || 0} / {totalStages}
                              </Text>
                            </Td>
                            <Td display={{ base: "none", xl: "table-cell" }}>
                              <Badge
                                colorScheme="purple"
                                size={{ base: "sm", md: "md" }}
                              >
                                {approval.workflowType}
                              </Badge>
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                              display={{ base: "none", xl: "table-cell" }}
                            >
                              {new Date(
                                approval.submittedAt
                              ).toLocaleDateString()}
                            </Td>
                            <Td>
                              <Button
                                size={{ base: "xs", md: "sm" }}
                                colorScheme="blue"
                                onClick={() =>
                                  handleApprovalClick(approval.offerId)
                                }
                              >
                                Review
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </TabPanel>

            {/* All Approvals Tab */}
            <TabPanel px={{ base: 0, md: 4 }}>
              <VStack
                spacing={4}
                align="stretch"
                mb={4}
                px={{ base: 2, md: 0 }}
              >
                <HStack
                  flexDirection={{ base: "column", sm: "row" }}
                  spacing={{ base: 2, sm: 4 }}
                  width="100%"
                >
                  <Input
                    placeholder="Search by Offer ID or Candidate..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size={{ base: "sm", md: "md" }}
                    width={{ base: "100%", sm: "auto" }}
                    flex={{ base: "1", sm: "initial" }}
                    maxW={{ sm: "400px" }}
                  />
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    size={{ base: "sm", md: "md" }}
                    width={{ base: "100%", sm: "auto" }}
                    maxW={{ sm: "200px" }}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired</option>
                  </Select>
                </HStack>
              </VStack>

              {filteredApprovals.length === 0 ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    No approvals found
                  </Text>
                </Alert>
              ) : (
                <Box overflowX="auto" pb={4}>
                  <Table variant="simple" size={{ base: "sm", md: "md" }}>
                    <Thead>
                      <Tr>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Offer ID
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Candidate
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", md: "table-cell" }}
                        >
                          Position
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", lg: "table-cell" }}
                        >
                          CTC
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Status
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", xl: "table-cell" }}
                        >
                          Workflow
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", lg: "table-cell" }}
                        >
                          Stage
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                          display={{ base: "none", xl: "table-cell" }}
                        >
                          Submitted
                        </Th>
                        <Th
                          fontSize={{ base: "xs", md: "sm" }}
                          whiteSpace="nowrap"
                        >
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredApprovals.map((approval) => {
                        const {
                          offerId,
                          candidateName,
                          designation,
                          ctc,
                          totalStages,
                        } = extractOfferData(approval);

                        return (
                          <Tr key={approval._id}>
                            <Td
                              fontWeight="semibold"
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                            >
                              {offerId}
                            </Td>
                            <Td fontSize={{ base: "xs", md: "sm" }}>
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">{candidateName}</Text>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  display={{ base: "block", md: "none" }}
                                >
                                  {designation}
                                </Text>
                              </VStack>
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              display={{ base: "none", md: "table-cell" }}
                            >
                              {designation}
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                              display={{ base: "none", lg: "table-cell" }}
                            >
                              {ctc ? `₹${ctc.toLocaleString()}` : "N/A"}
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={getStatusColor(
                                  approval.approvalStatus
                                )}
                                fontSize={{ base: "2xs", md: "xs" }}
                              >
                                {approval.approvalStatus || "N/A"}
                              </Badge>
                            </Td>
                            <Td display={{ base: "none", xl: "table-cell" }}>
                              <Badge
                                colorScheme="purple"
                                fontSize={{ base: "2xs", md: "xs" }}
                              >
                                {approval.workflowType || "N/A"}
                              </Badge>
                            </Td>
                            <Td display={{ base: "none", lg: "table-cell" }}>
                              <Text fontSize={{ base: "xs", md: "sm" }}>
                                {approval.currentStage || 0} / {totalStages}
                              </Text>
                            </Td>
                            <Td
                              fontSize={{ base: "xs", md: "sm" }}
                              whiteSpace="nowrap"
                              display={{ base: "none", xl: "table-cell" }}
                            >
                              {approval.submittedAt
                                ? new Date(
                                    approval.submittedAt
                                  ).toLocaleDateString()
                                : "N/A"}
                            </Td>
                            <Td>
                              <Button
                                size={{ base: "xs", md: "sm" }}
                                variant="outline"
                                colorScheme="blue"
                                onClick={() =>
                                  handleApprovalClick(approval.offerId)
                                }
                              >
                                View
                              </Button>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {selectedOffer && (
        <OfferApprovalModal
          isOpen={isOpen}
          onClose={onClose}
          offer={selectedOffer}
          onApprovalComplete={handleApprovalComplete}
        />
      )}
    </Container>
  );
};

export default OfferApprovalsPage;
